import { NavigateFunction } from "react-router-dom";
import { LoginResponse } from "../types/guardian";
import { setTermsAccepted, setHasProfile, isAthlete, isGuardian, isPolaris } from "./auth";
import { loginRoleFromSources, resolvePostLoginPath, takePostLoginPath } from "./loginRedirect";
import { resolveGuardianPostLoginPath } from "./viewedAthlete";

/**
 * Finishes a successful login (direct first login, or after MFA verification):
 * persists non-sensitive metadata, refreshes {@link AuthContext}, then routes the user
 * to the correct landing page for their role. No JWT is ever stored client-side; the
 * session lives in the HttpOnly cookie set by the backend.
 */
export async function completeLoginSession(
    loginData: LoginResponse,
    navigate: NavigateFunction,
    refreshAuth: () => Promise<void>
): Promise<void> {
    // Store only non-sensitive data in localStorage (no JWT token)
    if (loginData.userId != null) localStorage.setItem("loggedInUserId", String(loginData.userId));
    if (loginData.userId != null) localStorage.setItem("userId", String(loginData.userId));
    localStorage.setItem("authProvider", "password");

    // Store user role/profileType if provided by backend
    const userRole = loginData.profileType || loginData.role || loginData.userType;
    if (userRole) {
        localStorage.setItem("userRole", userRole);
    }

    if (loginData.hasLoggedInBefore !== undefined) {
        localStorage.setItem("hasLoggedInBefore", String(loginData.hasLoggedInBefore));
    }

    if (loginData.hasProfile !== undefined) {
        setHasProfile(loginData.hasProfile);
    }

    setTermsAccepted(loginData.termsAndConditionsAccepted ?? false);

    // Store guardian athlete data if user is a guardian
    const isGuardianUser = loginData.profileType === "GUARDIAN" || loginData.role === "GUARDIAN";
    if (isGuardianUser && loginData.guardianAthletes) {
        localStorage.setItem("guardianAthletes", JSON.stringify(loginData.guardianAthletes));
    }

    // Refresh AuthContext to load backend-validated role and update localStorage
    await refreshAuth();

    const next = takePostLoginPath();
    const role = loginRoleFromSources({
        profileType: loginData.profileType,
        role: loginData.role,
        userType: loginData.userType,
        isGuardian: isGuardian() || isGuardianUser,
        isPolaris: isPolaris(),
        isAthlete: isAthlete(),
    });

    if (role === "GUARDIAN") {
        navigate(resolveGuardianPostLoginPath(next));
        return;
    }

    const path = resolvePostLoginPath(role, next, loginData.hasProfile);
    if (path === "/add-admin-profile") {
        navigate(path, { state: { firstTimeSetup: true } });
        return;
    }
    navigate(path);
}
